// Central ledger — the Dummy Pot.
//
// This module is the single source of truth for OC balances, pledges, and
// the audit trail. When Supabase env vars are configured, writes go
// through to public.profiles / public.pledges; the localStorage cache
// stays in sync so useUserBalance / useAuditLog keep firing the moment
// a write lands (no polling, no round-trip-per-render). When Supabase
// isn't wired up the module degrades to pure localStorage so local dev
// and unconfigured previews still work.

import { useEffect, useState } from 'react';
import { supabase, isSupabaseEnabled } from './supabaseClient';
import { calcDivergencePoints } from './divergence';
import { IS_STRIPPED_DOWN } from './mode';
import { IdentityLevel } from './identity';

// ─── Types ───────────────────────────────────────────────────────────────

export interface Pledge {
  user_id:         string;
  // Market this pledge belongs to. FK to public.markets(id) on the
  // Supabase side. Required since I-006 closure (migration
  // 20260501000001_pledges_market_id.sql); the database now enforces
  // NOT NULL + FK so settlement scripts can filter by market without
  // relying on outcome_name uniqueness.
  market_id:       string;
  outcome:         string;
  units:           number;
  price_at_pledge: number;
  timestamp:       string; // ISO 8601
  // Tier-scaled protocol fee. Seeker/Observer = 1%, Citizen/Sovereign
  // = 0.2% (see TRADE_FEE_BY_TIER in lib/identity). Optional for
  // back-compat with cached ledger entries written before the schedule
  // landed; `recordPledgeRemote` fills it for every new row.
  trade_fee?:      number;
  // Effective Oracle Weight recorded at pledge time:
  //   calculated_weight = units × price × weightMultiplierForTier(tier)
  // See PROTOCOL_SPEC.md §1 (Multiplier Engine). This is the number
  // that downstream resolution / reputation yield should sum — not raw
  // units. Optional for back-compat; always populated on new rows.
  calculated_weight?: number;
  // ── Parimutuel pricing snapshot (Session B, 2026-05-13) ─────────────
  // Returned by place_pledge RPC per the I-042 / I-044 / I-047
  // integrated-slippage model. Optional for back-compat with cached
  // pledges written before the Supabase port; always populated on new
  // RPC-backed rows. The legacy `units` and `price_at_pledge` fields
  // above mirror `payout_units` and `effective_price` respectively
  // until the follow-up migration drops the legacy columns.
  pre_pledge_price?:  number;
  effective_price?:   number;
  post_pledge_price?: number;
  payout_units?:      number;
  slippage_pct?:      number | null;  // NULL in discovery territory (pre_price = 0)
  is_operator_seed?:  boolean;        // TRUE for per-outcome seed pledges from seed_market
}

// ── Slippage-cap rejection (Session B, 2026-05-13) ─────────────────────
//
// The place_pledge RPC rejects pledges exceeding the slippage cap with
// a structured DETAIL payload carrying the four prices and the
// computed slippage. The form catches this error, surfaces the prices
// to the user, and offers a "retry with override" button per the
// 2026-05-13 Session B go-brief §3(a) shape (iii). Re-call
// recordPledgeRemote with overrideSlippageCap = true to bypass.
export interface SlippageCapDetails {
  pre_pledge_price:  number;
  effective_price:   number;
  post_pledge_price: number;
  slippage_pct:      number;
  slippage_cap:      number;
}

export class SlippageCapError extends Error {
  details: SlippageCapDetails;
  constructor(message: string, details: SlippageCapDetails) {
    super(message);
    this.name = 'SlippageCapError';
    this.details = details;
  }
}

export interface AuditEntry {
  date:   string; // ISO 8601
  action: string;
  amount: number; // signed OC (positive = credit, negative = debit)
}

interface LedgerState {
  balances: Record<string, number>;
  pledges:  Pledge[];
  audit:    Record<string, AuditEntry[]>;  // user_id → entries
}

// ─── Storage ─────────────────────────────────────────────────────────────

const STORAGE_KEY = 'agoracle_ledger_v1';

// v2.1.0 Unified Meritocracy — one-time Genesis Grant on first wallet
// connect. 1,000 OC earmarked exclusively for Pledging. The 500 OC
// Minting Bond is *not* reserved here — it only comes into play once
// Level 2 (Citizen) is unlocked, and is drawn from the Mint Bond Pool
// managed by lib/mintedMarkets.ts, not this ledger.
export const GENESIS_GRANT_OC = 1000;

function empty(): LedgerState {
  return { balances: {}, pledges: [], audit: {} };
}

function readState(): LedgerState {
  if (typeof window === 'undefined') return empty();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as LedgerState;
    return {
      balances: parsed.balances ?? {},
      pledges:  parsed.pledges  ?? [],
      audit:    parsed.audit    ?? {},
    };
  } catch {
    return empty();
  }
}

const listeners = new Set<() => void>();

function writeState(s: LedgerState): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  listeners.forEach(fn => { try { fn(); } catch {} });
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

// ─── Readers ─────────────────────────────────────────────────────────────

export function getUserBalance(userId: string): number {
  return readState().balances[userId] ?? 0;
}

export function getAuditLog(userId: string, limit = 5): AuditEntry[] {
  const all = readState().audit[userId] ?? [];
  return all.slice(-limit).reverse();
}

export function getPledges(userId: string): Pledge[] {
  return readState().pledges.filter(p => p.user_id === userId);
}

// ─── Writers ─────────────────────────────────────────────────────────────

export function recordPledge(pledge: Pledge): void {
  const s    = readState();
  const cost = pledge.units * pledge.price_at_pledge;
  s.pledges.push(pledge);
  s.balances[pledge.user_id] = Math.max(0, (s.balances[pledge.user_id] ?? 0) - cost);
  s.audit[pledge.user_id]    = [
    ...(s.audit[pledge.user_id] ?? []),
    { date: pledge.timestamp, action: `Pledge · ${pledge.outcome}`, amount: -cost },
  ];
  writeState(s);
}

// ─── Supabase-backed writers ─────────────────────────────────────────────
// Mutate public.profiles / public.pledges on the hosted DB, then update
// the local cache so listeners (useUserBalance, useAuditLog, the
// balance pill) repaint immediately — no need to wait for realtime.

// Founding balance for a brand-new profile is the Genesis Grant —
// 1,000 OC for Pledging. See GENESIS_GRANT_OC above.
const FOUNDING_BALANCE = GENESIS_GRANT_OC;

function setLocalBalance(userId: string, balance: number): void {
  const s = readState();
  s.balances[userId] = balance;
  writeState(s);
}

function appendAudit(userId: string, action: string, amount: number, date = new Date().toISOString()): void {
  const s = readState();
  s.audit[userId] = [...(s.audit[userId] ?? []), { date, action, amount }];
  writeState(s);
}

// Ensures a profile row exists for `userId`. If absent, inserts one with
// the Genesis Grant (1,000 OC for Pledging) and writes an audit entry.
// Returns the balance now attached to the profile.
export async function ensureProfile(userId: string): Promise<number> {
  if (!isSupabaseEnabled() || !supabase) {
    const existing = readState().balances[userId];
    if (existing === undefined) {
      // Offline-only bootstrap: grant the Genesis Grant directly.
      const s = readState();
      const now = new Date().toISOString();
      s.balances[userId] = GENESIS_GRANT_OC;
      s.audit[userId] = [
        ...(s.audit[userId] ?? []),
        { date: now, action: 'Genesis Grant · 1,000 OC for Pledging', amount: GENESIS_GRANT_OC },
      ];
      writeState(s);
    }
    return getUserBalance(userId);
  }

  // Schema: profiles(id TEXT PK, username TEXT, balance NUMERIC).
  // Rows are keyed on the Ghost Citizen string directly so the TEXT
  // column matches without uuid casting.
  const { data, error } = await supabase
    .from('profiles')
    .select('balance')
    .eq('id', userId)
    .maybeSingle();

  if (error) throw new Error(`[ensureProfile:select] ${error.message}`);

  if (!data) {
    // No profile yet — insert with the founding balance. username mirrors
    // the id; cleaner than leaving it null and still satisfies any NOT
    // NULL constraint on the column.
    //
    // Session B recovery (2026-05-13, Phase C revision-2): identity_level
    // defaults to CITIZEN (level 2) on Arena (IS_STRIPPED_DOWN) so the
    // server-side profile matches the cockpit's auto-promote behaviour
    // per DECISIONS.md 2026-05-05 (Citizen-by-default for the Arena
    // sandbox release shape) and clears the place_pledge identity_level
    // gate (>= 1). On Live (IS_STRIPPED_DOWN = false), the default
    // stays at 0 (SEEKER) so Civic Pass identity verification is the
    // real gate. Inventory §6 / gate-22 Finding 1.
    const initialIdentityLevel = IS_STRIPPED_DOWN ? IdentityLevel.CITIZEN : 0;
    const { error: insertErr } = await supabase
      .from('profiles')
      .insert({
        id:             userId,
        username:       userId,
        balance:        FOUNDING_BALANCE,
        identity_level: initialIdentityLevel,
      });
    if (insertErr) throw new Error(`[ensureProfile:insert] ${insertErr.message}`);

    setLocalBalance(userId, FOUNDING_BALANCE);
    appendAudit(userId, 'Genesis Grant · 1,000 OC for Pledging', FOUNDING_BALANCE);
    return FOUNDING_BALANCE;
  }

  // Profile exists. Post-Ledger-Shield (migration 20260425), profiles.balance
  // is server-derived via the balance_after_mutation trigger:
  //     balance = 1000 − Σ pledges.total_cost + Σ settlements.amount
  // The client treats it as read-only — no derivation, no self-heal.
  // Drift detection is a server-side concern now (a future
  // audit_balance_drift() reconciliation job).
  const balance = typeof data.balance === 'number' ? data.balance : FOUNDING_BALANCE;
  setLocalBalance(userId, balance);
  return balance;
}

// Records a pledge through Supabase via the place_pledge RPC. The RPC
// runs the I-042 integrated-slippage math, validates the outcome
// exists in market_outcomes, checks identity_level >= 1 per §3(b),
// rejects if slippage exceeds the cap unless override is set, and
// atomically writes the pledge row. The pledges_recompute_balance
// trigger then updates profiles.balance.
//
// Optimistic UI: the local balance is debited, the pledge is pushed
// into the local audit, *before* the network round-trip. If the RPC
// rejects (slippage cap, identity gate, market closed, etc.), we
// roll the local ledger back to the prior server-verified state.
//
// On success the function returns an enriched Pledge with the
// pre/effective/post prices and payout_units populated from the RPC
// response — the form uses these for the four-price display.
//
// Slippage-cap rejection raises SlippageCapError with the four
// prices in `details`; callers can present a "retry with override"
// affordance and re-invoke with overrideSlippageCap = true.
//
// Session B, 2026-05-13 — switches from direct .from('pledges').insert
// (legacy path) to .rpc('place_pledge') per the Session A migration's
// new RPC interface.
export async function recordPledgeRemote(
  pledge: Pledge,
  overrideSlippageCap: boolean = false,
): Promise<Pledge> {
  // The stake in OC. The form may pass this either as the legacy
  // (units × price_at_pledge) product or — once the form flips to
  // OC-stake-as-primary input — directly as units × price_at_pledge
  // where units is the cockpit's display-derived value. Either way
  // the product is the actual OC stake (p_amount on the RPC).
  const stake = pledge.units * pledge.price_at_pledge;

  if (!isSupabaseEnabled() || !supabase) {
    recordPledge(pledge);
    return pledge;
  }

  // Snapshot for rollback.
  const snapshot = readState();
  const priorBalance = snapshot.balances[pledge.user_id];
  const priorPledges = snapshot.pledges;
  const priorAudit   = snapshot.audit[pledge.user_id] ?? [];

  // Optimistic debit. Use the local balance if we have one, otherwise
  // fall back to the founding balance so the pill still drops even on
  // a cold cache.
  const base       = priorBalance ?? FOUNDING_BALANCE;
  const optimistic = Math.max(0, base - stake);
  {
    const s = readState();
    s.balances[pledge.user_id] = optimistic;
    s.pledges = [...s.pledges, pledge];
    s.audit[pledge.user_id] = [
      ...priorAudit,
      { date: pledge.timestamp, action: `Pledge · ${pledge.outcome}`, amount: -stake },
    ];
    writeState(s);
  }

  function rollback(): void {
    const s = readState();
    if (priorBalance === undefined) delete s.balances[pledge.user_id];
    else                             s.balances[pledge.user_id] = priorBalance;
    s.pledges           = priorPledges;
    s.audit[pledge.user_id] = priorAudit;
    writeState(s);
  }

  try {
    // Call place_pledge RPC. Per Session A go-brief §3(a) the auth
    // model is "trust the caller's user_id" — the RPC takes p_user_id
    // as a parameter. I-045's auth.uid()-bound hybrid lands later.
    const { data, error: rpcErr } = await supabase.rpc('place_pledge', {
      p_market_id:         pledge.market_id,
      p_outcome_name:      pledge.outcome,
      p_amount:            stake,
      p_user_id:           pledge.user_id,
      p_slippage_cap:      0.05,
      p_slippage_override: overrideSlippageCap,
    });

    if (rpcErr) {
      // The slippage cap check raises EXCEPTION with ERRCODE
      // 'check_violation' (23514) and a JSON DETAIL payload carrying
      // the four prices. Surface as SlippageCapError so the form can
      // catch it specifically and offer retry-with-override.
      if (rpcErr.code === '23514' && rpcErr.details) {
        try {
          const parsed = JSON.parse(rpcErr.details) as SlippageCapDetails;
          throw new SlippageCapError(rpcErr.message, parsed);
        } catch (parseErr) {
          if (parseErr instanceof SlippageCapError) throw parseErr;
          // DETAIL wasn't parseable JSON — fall through to generic.
        }
      }
      throw new Error(`[recordPledgeRemote:rpc] ${rpcErr.message}`);
    }

    // RPC returns JSON with the four-price snapshot + payout_units.
    // Shape: { pledge_id, message, market_id, outcome_name, stake,
    //          pre_pledge_price, effective_price, post_pledge_price,
    //          payout_units, slippage_pct }
    const rpcResult = data as {
      pledge_id?:         string;
      pre_pledge_price?:  number;
      effective_price?:   number;
      post_pledge_price?: number;
      payout_units?:      number;
      slippage_pct?:      number | null;
    } | null;

    const enriched: Pledge = {
      ...pledge,
      pre_pledge_price:  rpcResult?.pre_pledge_price,
      effective_price:   rpcResult?.effective_price,
      post_pledge_price: rpcResult?.post_pledge_price,
      payout_units:      rpcResult?.payout_units,
      slippage_pct:      rpcResult?.slippage_pct ?? null,
      is_operator_seed:  false,
    };

    // Replace the optimistic pledge entry in the local cache with the
    // enriched version so subsequent reads have the four-price snapshot.
    {
      const s = readState();
      const idx = s.pledges.findIndex(
        p => p.user_id === pledge.user_id
          && p.market_id === pledge.market_id
          && p.outcome === pledge.outcome
          && p.timestamp === pledge.timestamp,
      );
      if (idx >= 0) {
        s.pledges = [...s.pledges];
        s.pledges[idx] = enriched;
        writeState(s);
      }
    }

    // Post-Ledger-Shield: the pledges_recompute_balance trigger has now
    // updated profiles.balance atomically. Read it back so the local
    // cache matches server truth without waiting on the realtime
    // channel — useProfileRealtime will pick up the same value moments
    // later, which is harmless (setLocalBalance is idempotent).
    const { data: profile, error: readErr } = await supabase
      .from('profiles')
      .select('balance')
      .eq('id', pledge.user_id)
      .maybeSingle();
    if (readErr) throw new Error(`[recordPledgeRemote:read] ${readErr.message}`);

    const next = typeof profile?.balance === 'number'
      ? profile.balance
      : Math.max(0, (priorBalance ?? FOUNDING_BALANCE) - stake);
    setLocalBalance(pledge.user_id, next);

    return enriched;
  } catch (err) {
    rollback();
    throw err;
  }
}

// Subscribes to realtime UPDATEs on public.profiles for `userId` and
// mirrors any external balance changes into the local ledger cache.
// Fires for every update — including our own, which is harmless because
// setLocalBalance is idempotent. Safe to call when Supabase isn't wired;
// the hook silently no-ops.
export function useProfileRealtime(userId: string): void {
  useEffect(() => {
    if (!userId || !isSupabaseEnabled() || !supabase) return;
    const channel = supabase
      .channel(`agoracle:profile:${userId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
        (payload: { new?: { balance?: unknown } }) => {
          const raw = payload.new?.balance;
          const b   = typeof raw === 'number' ? raw : Number(raw);
          if (!Number.isNaN(b)) setLocalBalance(userId, b);
        },
      )
      .subscribe();
    return () => {
      if (supabase) supabase.removeChannel(channel);
    };
  }, [userId]);
}

// ─── React hooks ─────────────────────────────────────────────────────────

export function useUserBalance(userId: string): number {
  const [b, setB] = useState<number>(() => getUserBalance(userId));
  useEffect(() => {
    setB(getUserBalance(userId));
    return subscribe(() => setB(getUserBalance(userId)));
  }, [userId]);
  return b;
}

export function useAuditLog(userId: string, limit = 5): AuditEntry[] {
  const [a, setA] = useState<AuditEntry[]>(() => getAuditLog(userId, limit));
  useEffect(() => {
    setA(getAuditLog(userId, limit));
    return subscribe(() => setA(getAuditLog(userId, limit)));
  }, [userId, limit]);
  return a;
}

// Threshold an Observer must reach to unlock Level 2 (Citizen). Chosen
// at 100 DP so a handful of accurate pledges close the gap — the point
// is to make the path visible, not to gate it aggressively. Surfaced
// from here so the sidebar progress bar and any future promotion hook
// read the same number.
export const CITIZEN_DP_THRESHOLD = 100;

// v2.3.1 Minting Cockpit Activation — the SIMULATE CITIZENSHIP button
// injects a synthetic DP floor so the sandbox demo can exercise the
// Dynamic Bond discount without first grinding out 150 pledges. The
// override persists under `agoracle_simulated_dp` and feeds useUserDP
// via max(derived, simulated). RESET TO OBSERVER clears it.
const SIMULATED_DP_KEY      = 'agoracle_simulated_dp';
const SIMULATED_DP_EVENT    = 'agoracle:simulated-dp-change';
export const SIMULATED_CITIZEN_DP = 150;

export function getSimulatedDP(userId: string): number {
  if (typeof window === 'undefined' || !userId) return 0;
  try {
    const raw = window.localStorage.getItem(`${SIMULATED_DP_KEY}:${userId}`);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch { return 0; }
}

export function setSimulatedDP(userId: string, dp: number | null): void {
  if (typeof window === 'undefined' || !userId) return;
  try {
    const key = `${SIMULATED_DP_KEY}:${userId}`;
    if (dp === null || dp <= 0) window.localStorage.removeItem(key);
    else                        window.localStorage.setItem(key, String(dp));
    window.dispatchEvent(new CustomEvent(SIMULATED_DP_EVENT, { detail: { userId } }));
  } catch { /* storage denied */ }
}

export function clearSimulatedDP(userId: string): void {
  setSimulatedDP(userId, null);
}

// Live DP for a citizen. Aggregates Σ units × (1 − price) over every
// pledge the user has placed. Supabase is the source of truth when
// wired; the local ledger fallback keeps the sidebar honest in offline
// previews. Both paths subscribe so the number streams as pledges land.
//
// v2.3.1 — if a simulated-DP override is in play for this user (via
// SIMULATE CITIZENSHIP), return max(derived, simulated) so the Dynamic
// Bond can exercise the discounted-path without real pledges on file.
export function useUserDP(userId: string): number {
  const [derived,   setDerived]   = useState<number>(0);
  const [simulated, setSimulated] = useState<number>(() => getSimulatedDP(userId));

  useEffect(() => {
    setSimulated(getSimulatedDP(userId));
    if (typeof window === 'undefined') return;
    const onChange = (e: Event): void => {
      const detail = (e as CustomEvent<{ userId?: string }>).detail;
      if (!detail || detail.userId === userId) setSimulated(getSimulatedDP(userId));
    };
    window.addEventListener(SIMULATED_DP_EVENT, onChange as EventListener);
    return () => window.removeEventListener(SIMULATED_DP_EVENT, onChange as EventListener);
  }, [userId]);

  useEffect(() => {
    if (!userId) { setDerived(0); return; }
    let cancelled = false;

    if (!isSupabaseEnabled() || !supabase) {
      const recompute = (): void => {
        const all = readState().pledges.filter(p => p.user_id === userId);
        let total = 0;
        for (const p of all) total += calcDivergencePoints(p.units, p.price_at_pledge);
        if (!cancelled) setDerived(total);
      };
      recompute();
      const unsub = subscribe(recompute);
      return () => { cancelled = true; unsub(); };
    }

    const hydrate = async (): Promise<void> => {
      if (!supabase) return;
      // Session B (2026-05-13): switch read path from legacy
      // `units, price_at_pledge` columns to the new parimutuel
      // names `payout_units, effective_price`. Values are identical
      // via the legacy mirror in place_pledge (until the legacy-
      // column-drop follow-up migration lands), but the cockpit no
      // longer depends on the legacy columns existing. Per Session
      // A findings §6 future-migration target #1.
      //
      // Arena enrolment (2026-05-15, brief §3.2 #1): aggregate over
      // the full effective-owner closure rather than the raw
      // `user_id = userId` set. The public.effective_owner view
      // returns one row per profile mapped to a given enrolled
      // owner — for an unclaimed Ghost or an enrolled identity with
      // no claims, that's just the userId itself; for an enrolled
      // identity that has claimed N Ghosts, it's the (N+1) ids
      // contributing to that owner's DP.
      const { data: ownerRows, error: ownerErr } = await supabase
        .from('effective_owner')
        .select('profile_id')
        .eq('effective_profile_id', userId);
      if (cancelled) return;
      if (ownerErr || !ownerRows) {
        // Fail closed: zero DP rather than risk over-attributing to
        // the wrong identity if the view query fails.
        if (!ownerErr) setDerived(0);
        return;
      }
      const profileIds = (ownerRows as Array<{ profile_id: string }>).map(r => r.profile_id);
      if (profileIds.length === 0) {
        // Newly minted Ghost that hasn't hit ensureProfile yet — no
        // profile row, therefore no pledges. The realtime sub will
        // re-hydrate once the first pledge lands.
        setDerived(0);
        return;
      }
      const { data, error } = await supabase
        .from('pledges')
        .select('payout_units, effective_price')
        .in('user_id', profileIds);
      if (cancelled || error || !data) return;
      let total = 0;
      for (const row of data as Array<{ payout_units: number | string | null; effective_price: number | string | null }>) {
        total += calcDivergencePoints(
          Number(row.payout_units) || 0,
          Number(row.effective_price) || 0,
        );
      }
      setDerived(total);
    };
    void hydrate();

    // Realtime channel: subscribe to changes on the active id only.
    // New pledges land under the active id (Ghost pre-enrolment or
    // enrolled post-enrolment); historical pledges under claimed
    // Ghost ids are append-only (Ledger Shield) and never mutate
    // after the claim. Initial hydrate above already captured them
    // through the effective-owner closure.
    const channel = supabase
      .channel(`agoracle:pledges:self:${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pledges', filter: `user_id=eq.${userId}` },
        () => { void hydrate(); },
      )
      .subscribe();

    return () => {
      cancelled = true;
      if (supabase) supabase.removeChannel(channel);
    };
  }, [userId]);

  // Session B Phase C (2026-05-13): when IS_STRIPPED_DOWN, return 0
  // unconditionally. DP is held back per DECISIONS.md 2026-05-13
  // I-046; consumers (Leaderboard, SystemBriefingSidebar) are now
  // gated. Hooks still run (rules-of-hooks compliant) but the value
  // is zeroed. Inventory §6 / §9 item 14.
  if (IS_STRIPPED_DOWN) return 0;
  return Math.max(derived, simulated);
}
